/**
 * Entrevista — 10 perguntas, 100 pontos, mínimo 82.
 *
 * O agente não decide quem entra: regista o que ouviu e a pontuação decide.
 * Por isso o total aparece ao vivo enquanto responde — ele vê a entrevista a
 * formar-se, e não descobre o resultado só no fim.
 *
 * Perguntas eliminatórias (sem BI, não aceita os 10%, sem telemóvel) reprovam
 * sozinhas, e o ecrã di-lo de imediato em vez de deixar continuar às cegas.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

const FieldInterview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [notas, setNotas] = useState('');
  const [erro, setErro] = useState('');
  const [busy, setBusy] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    api.get('/operations/interview/questions')
      .then((r) => setQ(r.data))
      .catch(() => setErro('Não foi possível carregar o questionário.'));
  }, []);

  if (!q) {
    return (
      <div className="flex justify-center py-16">
        {erro ? <p className="text-red-400 text-sm">{erro}</p> : <Loader2 className="w-6 h-6 animate-spin text-slate-600" />}
      </div>
    );
  }

  const respondidas = q.perguntas.filter((p) => respostas[p.id]).length;
  const completo = respondidas === q.perguntas.length;

  // Total provisório: só conta o que já foi respondido, por isso sobe à
  // medida que a entrevista avança em vez de começar em 100 e descer.
  const parcial = q.perguntas.reduce((soma, p) => {
    const o = p.opcoes.find((x) => x.valor === respostas[p.id]);
    return soma + (o ? Math.round(p.peso * o.pontos) : 0);
  }, 0);

  const eliminatoria = q.perguntas.find((p) => {
    const o = p.opcoes.find((x) => x.valor === respostas[p.id]);
    return o?.eliminatoria;
  });

  const submeter = async () => {
    setBusy(true); setErro('');
    try {
      const { data } = await api.post(`/operations/workers/${id}/interview`, { respostas, notas });
      setResultado(data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível registar a entrevista.');
    } finally { setBusy(false); }
  };

  if (resultado) {
    const ok = resultado.aprovado;
    return (
      <div className="text-center py-10 space-y-4">
        {ok
          ? <CheckCircle2 className="w-16 h-16 mx-auto text-green-400" />
          : <XCircle className="w-16 h-16 mx-auto text-red-400" />}
        <div className="text-4xl font-bold tabular-nums">{resultado.pontuacao}<span className="text-xl text-slate-500">/100</span></div>
        <p className={`font-semibold ${ok ? 'text-green-400' : 'text-red-400'}`}>
          {ok ? 'Aprovado — passou à formação.' : 'Não aprovado.'}
        </p>
        {resultado.eliminado_por && (
          <p className="text-sm text-amber-300 max-w-sm mx-auto">
            Reprovação automática: <strong>{resultado.eliminado_por}</strong>
          </p>
        )}
        {!ok && !resultado.eliminado_por && (
          <p className="text-sm text-slate-400">Mínimo exigido: {resultado.nota_minima} pontos.</p>
        )}
        <div className="flex gap-2 justify-center pt-2">
          <button onClick={() => navigate('/admin/workers')}
            className="px-5 py-3 rounded-xl bg-slate-800 text-slate-200 font-semibold">Voltar à lista</button>
          {ok && (
            <button onClick={() => navigate(`/admin/workers/${id}/training`)}
              className="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold">Começar formação</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-28">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} aria-label="Voltar"
          className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-slate-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold leading-tight">Entrevista</h1>
          <p className="text-xs text-slate-500">Versão {q.versao} · mínimo {q.nota_minima}/100</p>
        </div>
      </div>

      {eliminatoria && (
        <div className="flex gap-2 p-3 rounded-xl bg-red-500/10 text-red-300 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>Esta resposta reprova o candidato, tenha a pontuação que tiver. Pode terminar na mesma — fica registado porquê.</span>
        </div>
      )}

      <ol className="space-y-4">
        {q.perguntas.map((p, i) => (
          <li key={p.id} className="p-3 rounded-xl border border-slate-800 bg-slate-900">
            <fieldset>
              <legend className="font-semibold text-slate-100 mb-2.5">
                <span className="text-slate-600 mr-1.5 tabular-nums">{i + 1}.</span>{p.texto}
                <span className="ml-2 text-xs font-normal text-slate-600">{p.peso} pts</span>
              </legend>
              <div className="space-y-2">
                {p.opcoes.map((o) => {
                  const escolhido = respostas[p.id] === o.valor;
                  return (
                    <label key={o.valor}
                      className={`flex items-center gap-3 min-h-[44px] px-3 rounded-lg border cursor-pointer
                        ${escolhido
                          ? o.eliminatoria
                            ? 'border-red-500/50 bg-red-500/10 text-red-200'
                            : 'border-blue-500/50 bg-blue-500/10 text-blue-100'
                          : 'border-slate-800 bg-slate-950 text-slate-300'}`}>
                      <input type="radio" name={p.id} value={o.valor} checked={escolhido}
                        onChange={() => setRespostas((r) => ({ ...r, [p.id]: o.valor }))}
                        className="w-4 h-4 accent-blue-500" />
                      <span className="flex-1 text-sm py-2">{o.rotulo}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </li>
        ))}
      </ol>

      <div>
        <label htmlFor="notas" className="block text-sm text-slate-400 mb-1">Observações (opcional)</label>
        <textarea id="notas" rows={3} value={notas} onChange={(e) => setNotas(e.target.value)}
          placeholder="O que não cabe nas perguntas."
          className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-200 outline-none focus:border-blue-500" />
      </div>

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      {/* Barra fixa: o total tem de estar sempre visível enquanto ele desce a lista. */}
      <div className="fixed bottom-[69px] inset-x-0 z-30 p-3 bg-slate-950 border-t border-slate-800 flex items-center gap-3">
        <div className="shrink-0">
          <div className="text-2xl font-bold tabular-nums leading-none">
            {parcial}<span className="text-sm text-slate-600">/100</span>
          </div>
          <div className="text-[11px] text-slate-500">{respondidas} de {q.perguntas.length} respondidas</div>
        </div>
        <button onClick={submeter} disabled={!completo || busy}
          className="flex-1 min-h-[48px] rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {completo ? 'Terminar entrevista' : 'Responda a todas'}
        </button>
      </div>
    </div>
  );
};

export default FieldInterview;
